/**
 * MyPositionBlock — User rank/score/stats widget
 *
 * Displays the current user's position in a hub leaderboard.
 * Configurable primary stats (Rank, Score) and secondary stats (Wins, Kills, etc.).
 *
 * Data source:
 *   - HUB_POINTS (default when surfaceSlug is available): fetches from hub member leaderboard
 *   - LEDGER: reads a LedgerBoard board — the server returns the caller's true
 *     myRank/myPoints regardless of page, so out-of-top-N users resolve correctly
 *   - COMPETITION: not yet implemented — shows placeholder values
 *
 * Only fetches leaderboard data when the user is authenticated (has fullToken).
 * For HUB_POINTS, users ranked outside the top 100 will see placeholder values
 * until a dedicated "my position" endpoint is added.
 *
 * Secondary stats with labels that don't match a known leaderboard field
 * (RANK, SCORE, POINTS, BALANCE, TOTAL EARNED) will display "—".
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { SurfaceMyPositionConfigResponse, SurfaceMyPositionStatItemResponse } from "@/hooks/hub/useGetHub";
import { useGetDropMemberLeaderboard, DropPointsLeaderboardEntry } from "@/hooks/drop/useGetDropMemberLeaderboard";
import { useGetLedgerStandings } from "@/hooks/ledger-boards/useGetLedgerStandings";
import "./MyPositionBlock.scss";

/**
 * The subset of a leaderboard entry the stat resolver needs — both
 * DropPointsLeaderboardEntry and the synthesized ledger position satisfy it.
 * totalEarned is optional because the ledger board has no such concept.
 */
interface PositionEntryLike {
    rank: number;
    balance: number;
    totalEarned?: number;
}

interface MyPositionBlockProps {
    config?: SurfaceMyPositionConfigResponse;
    sectionTitle?: string;
    surfaceSlug?: string;
}

/** Validates block IDs before use in querySelector to prevent CSS selector injection */
const SAFE_BLOCK_ID_RE = /^[a-zA-Z0-9_-]{1,100}$/;

const DEFAULT_PRIMARY_STATS: SurfaceMyPositionStatItemResponse[] = [
    { label: "RANK", valuePrefix: "#" },
    { label: "SCORE" },
];

/**
 * Resolve a stat's display value from a leaderboard entry.
 * Maps well-known labels (RANK, SCORE, POINTS, BALANCE, TOTAL EARNED)
 * to leaderboard fields. Unknown labels return "—".
 */
function resolveStatValue(stat: SurfaceMyPositionStatItemResponse, entry: PositionEntryLike | null): string {
    if (!entry) return "—";
    const label = stat.label.toUpperCase();
    if (label === "RANK") return String(entry.rank);
    if (label === "SCORE" || label === "POINTS" || label === "BALANCE") {
        return entry.balance.toLocaleString();
    }
    if (label === "TOTAL EARNED") return entry.totalEarned != null ? entry.totalEarned.toLocaleString() : "—";
    return "—";
}

export const MyPositionBlock = ({ config, sectionTitle, surfaceSlug }: MyPositionBlockProps) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const currentUserId = fullToken?.id;

    const title = config?.title || sectionTitle || "MY POSITION";
    const primaryStats = config?.primaryStats?.length ? config.primaryStats : DEFAULT_PRIMARY_STATS;
    const secondaryStats = config?.secondaryStats || [];
    const ctaText = config?.ctaText;
    const ctaTargetBlockId = config?.ctaTargetBlockId;

    // Only fetch leaderboard when user is authenticated
    const isLedger = config?.dataSource === "LEDGER" && !!config?.ledgerBoardId;
    const isHubPoints = !isLedger && (config?.dataSource === "HUB_POINTS" || !config?.dataSource && !!surfaceSlug);
    const shouldFetch = isHubPoints && !!currentUserId;
    const { data: leaderboardData } = useGetDropMemberLeaderboard(
        shouldFetch ? surfaceSlug : undefined,
        0,
        100,
    );

    /*
     * Ledger board — myRank/myPoints come back on any page, so limit 1 keeps
     * the payload minimal; this block never renders the standings list.
     */
    const { data: ledgerData } = useGetLedgerStandings(
        isLedger && currentUserId ? config?.ledgerBoardId : undefined,
        { limit: 1 },
    );

    // Find current user's entry in leaderboard
    const myEntry = useMemo((): PositionEntryLike | null => {
        if (!currentUserId) return null;
        if (isLedger) {
            if (ledgerData?.myRank == null) return null;
            return { rank: ledgerData.myRank, balance: ledgerData.myPoints ?? 0 };
        }
        if (!leaderboardData?.entries) return null;
        return leaderboardData.entries.find((e: DropPointsLeaderboardEntry) => e.userId === currentUserId) || null;
    }, [currentUserId, isLedger, ledgerData, leaderboardData?.entries]);

    const handleCtaClick = () => {
        if (ctaTargetBlockId && SAFE_BLOCK_ID_RE.test(ctaTargetBlockId)) {
            const target = document.querySelector(`[data-block-id="${ctaTargetBlockId}"]`);
            if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    };

    return (
        <div className="my-position">
            {/* Badge Header */}
            <div className="my-position__badge">
                {config?.profileImageUrl &&
                    <img
                        src={config.profileImageUrl}
                        alt=""
                        className="my-position__badge-icon"
                    />
                }
                <span className="my-position__badge-title">{title}</span>
            </div>

            {/* Primary Stats (Rank, Score) */}
            <div className="my-position__primary">
                {primaryStats.map((stat) =>
                    <div key={stat.label} className="my-position__stat my-position__stat--primary">
                        <span className="my-position__stat-label">{stat.label}</span>
                        <span className="my-position__stat-value">
                            {stat.valuePrefix || ""}{resolveStatValue(stat, myEntry)}
                        </span>
                    </div>)}
            </div>

            {/* Secondary Stats (Wins, Kills, etc.) */}
            {secondaryStats.length > 0 &&
                <div className="my-position__secondary">
                    {secondaryStats.map((stat) =>
                        <div key={stat.label} className="my-position__stat">
                            <span className="my-position__stat-label">{stat.label}</span>
                            <span className="my-position__stat-value">
                                {stat.valuePrefix || ""}{resolveStatValue(stat, myEntry)}
                            </span>
                        </div>)}
                </div>
            }

            {/* CTA Button */}
            {ctaText &&
                <button
                    type="button"
                    className="my-position__cta"
                    onClick={handleCtaClick}
                    disabled={!fullToken}
                >
                    {ctaText}
                </button>
            }
        </div>
    );
};
